import { Loader2, RefreshCw, AlertCircle } from 'lucide-react'

interface ProcessingOverlayProps {
  status: 'uploading' | 'creating' | 'processing' | 'error' | string
  progress: number
  fileName?: string | null
  onRetry?: () => void
  error?: string | null
  statusMessages?: Record<string, string>
}

const DEFAULT_STATUS_MESSAGES: Record<string, string> = {
  uploading: 'Uploading file...',
  creating: 'Creating product list...',
  processing: 'Processing data...',
  error: 'Processing failed',
}

/**
 * A reusable overlay component for displaying file upload/processing progress.
 * Shows loading spinner, progress bar, and error state with retry capability.
 */
export function ProcessingOverlay({
  status,
  progress,
  fileName,
  onRetry,
  error,
  statusMessages = DEFAULT_STATUS_MESSAGES,
}: ProcessingOverlayProps) {
  const isError = status === 'error'
  const statusText = statusMessages[status] || 'Processing...'

  return (
    <div className="bg-white p-6 rounded-[12px] border border-[#D6DAE1] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.09)]">
      <div className="flex flex-col items-center justify-center py-8 gap-4">
        {isError ? (
          <AlertCircle className="h-12 w-12 text-red-500" />
        ) : (
          <Loader2 className="h-12 w-12 text-blue-500 animate-spin" />
        )}

        <div className="text-center">
          <p className="text-[16px] font-semibold text-[#1C1C1C]">
            {statusText}
          </p>
          {fileName && (
            <p className="text-[14px] text-[#556374] mt-1">{fileName}</p>
          )}
          {error && <p className="text-[14px] text-red-600 mt-2">{error}</p>}
        </div>

        {!isError && (
          <div className="w-full max-w-xs">
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-500 transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-[12px] text-[#556374] text-center mt-2">
              {progress}% complete
            </p>
          </div>
        )}

        {isError && onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="flex items-center gap-2 px-4 py-2 bg-destructive text-destructive-foreground rounded-[6px] text-[14px] font-medium hover:bg-destructive/90 cursor-pointer transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            Retry Upload
          </button>
        )}
      </div>
    </div>
  )
}
